import { Box, Divider, Typography } from '@mui/material';
import { IconAvatar, ReemaThemeV2 } from '@act/storybook';
import { GoalType } from 'stories/containers/GoalsTracking/types';

interface GoalCardProps {
  id: string;
  goalType: GoalType;
  title: string;
  dueDate: string;
  onClick?: (id: string) => void;
}

export const GoalCard = ({
  id,
  goalType,
  title,
  dueDate,
  onClick,
}: GoalCardProps) => {
  const handleClick = () => {
    onClick?.(id);
  };

  const getIcon = () => {
    switch (goalType) {
      case 'survey':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconAvatar avatarType="survey" size={20} />
          </Box>
        );
      case 'need':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconAvatar avatarType="need" size={20} />
          </Box>
        );
      case 'consent':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconAvatar avatarType="consent" size={20} />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[100],
        border: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`,
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[200],
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <Box
        sx={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {getIcon()}
      </Box>
      <Box sx={{ width: '60%', minWidth: 0 }}>
        <Box
          sx={{
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px',
            backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[100],
            color: ReemaThemeV2.LIGHT_MODE.palette.navyShades[900],
            border: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.strokePrimary}`,
          }}
        >
          {goalType}
        </Box>
        <Typography
          variant="bodyLarge"
          sx={{
            fontWeight: 600,
            color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[800],
            marginBottom: '4px',
            lineHeight: 1.3,
            minWidth: !dueDate ? '300px' : 'auto',
          }}
        >
          {title}
        </Typography>
      </Box>
      {!!dueDate && (
        <>
          <Divider
            orientation="vertical"
            sx={{
              height: '80px',
              alignSelf: 'center',
              flexShrink: 0,
            }}
          />
          <Box sx={{ flexShrink: 0, textAlign: 'right', width: '20%' }}>
            <Typography
              variant="bodyMedium"
              sx={{
                color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[600],
                fontWeight: 500,
                textAlign: 'right',
              }}
            >
              Due {dueDate}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
